<template>
  <svg :viewBox="viewBox" :class="$style.graph">
    <g id="xGrid">
      <line
        :x1="multX * (padding - 1)"
        :x2="multX * (maxX + padding + 1)"
        :y1="-multY * padding"
        :y2="-multY * padding"
        stroke="#ccc"
      />
      <line
        v-for="label in labelsX"
        :x1="label.x"
        :x2="label.x"
        :y1="-multY * padding"
        :y2="-multY * padding + 3"
        stroke="#ccc"
      />
    </g>
    <g id="yGrid">
      <line
        :x1="multX * (padding - 1)"
        :x2="multX * (padding - 1)"
        :y1="multY * -(maxY + padding)"
        :y2="multY * -padding"
        stroke="#ccc"
      />
      <line
        v-for="label in labelsY"
        :x1="multX * (padding - 1)"
        :x2="multX * (maxX + padding + 1)"
        :y1="label.y"
        :y2="label.y"
        :class="$style.grid"
      />
    </g>
    <g :class="$style.labels">
      <text
        :x="multX * (maxX + minX + padding) * 0.5"
        :y="-multY * (maxY + padding)"
        :class="$style.labelTitle"
      >
        {{ statName }}
      </text>
      <text
        v-for="label in labelsX"
        :x="label.x"
        :y="label.y"
        text-anchor="middle"
      >
        {{ label.value }}
      </text>
    </g>
    <g :class="$style.labels">
      <text
        v-for="label in labelsY"
        :x="label.x"
        :y="label.y"
        text-anchor="middle"
      >
        {{ label.value }}
      </text>
    </g>
    <polyline
      fill="none"
      stroke="#0074d9"
      stroke-width="2"
      :points="points.map((point) => `${point.x} ${point.y}`).join(',')"
    />
    <circle
      v-for="point in points"
      fill="#0074d9"
      :cx="point.x"
      :cy="point.y"
      r="3"
      :class="$style.point"
    >
      <title>{{ point.range }}: {{ point.value }}</title>
    </circle>
  </svg>
</template>

<script>
export default {
  props: {
    statValues: {
      type: Array,
      required: true,
    },
    statName: {
      type: String,
      default: "",
    },
  },
  computed: {
    viewBox() {
      return `0 ${(this.maxY + this.padding * 2) * -this.multY} ${
        this.multX * (this.maxX + this.minX + this.padding * 2)
      } ${(this.maxY + this.padding * 2) * this.multY}`;
    },
    padding() {
      return 5;
    },
    maxY() {
      const values = Object.values(this.group).map(({ value }) => value);
      return Math.max(...(values.length > 0 ? values : [0]));
    },
    groupKeys() {
      return Object.keys(this.group);
    },
    minX() {
      const keys = this.groupKeys.length > 0 ? this.groupKeys : [0];
      return Math.min(...keys);
    },
    maxX() {
      const keys = this.groupKeys.length > 0 ? this.groupKeys : [0];
      return Math.max(...keys) - Math.min(...keys);
    },
    multipliers() {
      return [
        500 / (this.maxX + this.padding * 2),
        300 / (this.maxY + this.padding * 2),
      ];
    },
    multX() {
      return this.multipliers[0];
    },
    multY() {
      return this.multipliers[1];
    },
    points() {
      return Object.entries(this.group)
        .sort(([v1], [v2]) => v2 - v1)
        .map(([value, stat]) => {
          return {
            range: stat.range,
            value: stat.value,
            x: this.multX * (value - this.minX + this.padding),
            y: this.multY * -(stat.value + this.padding),
          };
        });
    },
    labelsX() {
      const numX = Object.keys(this.group).length;
      const labelsToShow = numX < 8 ? 3 : 5;
      const numLabels = Math.min(numX, labelsToShow);
      const labelsToSkip = Math.round(numX / numLabels);

      const groupValues = Object.values(this.group);
      return Array.from(new Array(numLabels), (_, n) => n * labelsToSkip).map(
        (index) => {
          const stat =
            index < groupValues.length
              ? groupValues[index]
              : groupValues[index - 1];
          return {
            x: (this.padding + index) * this.multX,
            value: stat.range,
            y: this.padding * -this.multY + 10,
          };
        }
      );
    },
    labelsY() {
      const skipLabels = Math.max(Math.trunc(this.maxY / 5), 1);
      const numLabels = Math.max(Math.trunc(this.maxY / skipLabels), 1);
      return Array.from(new Array(numLabels).keys(), (_, n) => n + 1).map(
        (index) => ({
          y: -(this.padding + index * skipLabels) * this.multY,
          value: index * skipLabels,
          x: (this.padding - 2) * this.multX,
        })
      );
    },
    group() {
      const min = Math.min(...this.statValues);
      const max = Math.max(...this.statValues);
      const bucketSize = Math.max(1, Math.round((max - min) / 10));
      return this.statValues.reduce((acc, value) => {
        const bucket = Math.trunc(value / bucketSize);
        const format = (num) =>
          Number.isInteger(num) ? num : num.toFixed(2) + "%";
        const bucketValue = acc[bucket] || {
          value: 0,
          range: "0-0",
          values: [],
        };
        const minValue = Math.min(value, ...bucketValue.values);
        const maxValue = Math.max(value, ...bucketValue.values);
        return {
          ...acc,
          [bucket]: {
            ...bucketValue,
            value: bucketValue.value + 1,
            values: [...bucketValue.values, value],
            range: `${format(minValue)}-${format(maxValue)}`,
          },
        };
      }, {});
    },
  },
};
</script>

<style lang="scss" module>
.graph {
  background-color: hsla(211.3, 28.2%, 33.3%, 0.9);
  width: calc(100vw - 16px);
  height: calc(60vw - 16px / 1.66666);
  max-width: 784px;
  max-height: 470px;
}
.labels {
  font-size: 0.5rem;
  stroke: hsl(224.2, 33.3%, 88.8%);
}
.label-title {
  font-weight: bold;
  text-transform: uppercase;
}
.grid {
  stroke: #ccc4;
  stroke-dasharray: 1 2;
  stroke-width: 1;
}

.point {
  fill: red;
}

.point:hover {
  stroke: yellow;
}
</style>
